import React, { useState, useEffect } from "react";
import TableHeader from "../Assets/TableHeader";
import TableBody from "../Assets/TableBody";
import LoadingCircle from "../Assets/LoadingCirlce";
import TableFooterPagination from "../Assets/TableFooterPagination";
import { HistoryAbout } from "../Types/Types";

interface AdminHistoryTableProps {
  history: HistoryAbout[];
  loading: boolean;
  error: string | null;
  onDelete: (id: string) => void;
  onEdit: (entry: HistoryAbout) => void;
  onSortChange: (field: string, order: string) => void;
  lang: string;
  onReorder?: (newOrder: HistoryAbout[]) => Promise<void>;
}

const AdminHistoryTable: React.FC<AdminHistoryTableProps> = ({
  history,
  loading,
  error,
  onDelete,
  onEdit,
  onSortChange,
  lang,
  onReorder,
}) => {
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [localData, setLocalData] = useState<HistoryAbout[]>(history);

  useEffect(() => {
    setLocalData(history);
  }, [history]);

  const totalPages = Math.ceil(localData.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = localData.slice(indexOfFirst, indexOfLast);

  const handleSort = (column: string) => {
    const newOrder = sortBy === column && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(column);
    setSortOrder(newOrder);
    onSortChange(column, newOrder);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRows = parseInt(e.target.value, 10);
    setRowsPerPage(newRows);
    setCurrentPage(1);
  };

  const handleReorder = async (reorderedCurrentPageData: HistoryAbout[]) => {
    const newFullOrder = [
      ...localData.slice(0, indexOfFirst),
      ...reorderedCurrentPageData,
      ...localData.slice(indexOfLast),
    ];
    setLocalData(newFullOrder);
    if (onReorder) {
      await onReorder(newFullOrder);
    }
  };

  if (loading) return <LoadingCircle />;
  if (error) return <div className="text-center py-4 text-red-500">{error}</div>;

  return (
    <div className="overflow-x-auto bg-white shadow-md rounded-lg">
      <table className="min-w-full table-auto">
        <TableHeader
          data={localData}
          handleSort={handleSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
        />
        <TableBody
          data={currentData}
          onDelete={onDelete}
          onEdit={onEdit}
          lang={lang}
          onReorder={handleReorder}
          editable={true}

        />
      </table>
      <TableFooterPagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default AdminHistoryTable;
